// Everyone's avatar URL by user id, shared by every <Avatar>. Filled from member lists, /api/me and "memberUpdated"
// events, so call sites only pass an id and a name and pictures appear (and change) everywhere at once.
import { useSyncExternalStore } from "react";

const images = new Map<string, string | null>();
const listeners = new Set<() => void>();
let version = 0;

/** Records people's current avatars (null: none, so the coloured initials show). */
export function rememberAvatars(people: { id: string; image?: string | null }[]) {
  let changed = false;
  for (const p of people) {
    const image = p.image ?? null;
    if (images.get(p.id) === image) continue;
    images.set(p.id, image);
    changed = true;
  }
  if (!changed) return;
  version++;
  for (const fn of listeners) fn();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export function useAvatar(id: string): string | null {
  useSyncExternalStore(subscribe, () => version);
  return images.get(id) ?? null;
}

// Bots (docs/bots.md): an emoji avatar by bot id, filled from the room's bots ("ready.bots", "bot" events).
const botAvatars = new Map<string, string>();
let botVersion = 0;
const botListeners = new Set<() => void>();

export function rememberBots(bots: { id: string; avatar: string }[]) {
  let changed = false;
  for (const b of bots) {
    if (botAvatars.get(b.id) === b.avatar) continue;
    botAvatars.set(b.id, b.avatar);
    changed = true;
  }
  if (!changed) return;
  botVersion++;
  for (const fn of botListeners) fn();
}

const subscribeBots = (fn: () => void) => {
  botListeners.add(fn);
  return () => botListeners.delete(fn);
};

/** A bot's avatar emoji (🤖 until the room says). */
export function useBotAvatar(id: string): string {
  useSyncExternalStore(subscribeBots, () => botVersion);
  return botAvatars.get(id) ?? "🤖";
}
